import { getConfigForImportedTest, getConfigForMeasuredTest, getConfigForTest } from 'Components/Worksheets/Config';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import { getMoistureContentPercent } from 'Components/Worksheets/MoistureContentWorksheet/BsEnIso/Config/index';
import qestIds from 'Components/Worksheets/QestIds';
import { FormatTypes } from 'Lib/Format';
import getDerivedFromMeasurementsConfig from '../../derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import { PreparationMethodType } from '../../types';
import getLabelConfig from './labelConfig';

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      ...getMoistureContentPercent('data.Preparation.AsReceivedWaterContent'),
      ...getConfigForMeasuredTest(
        qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
        'data.Preparation.AssociatedAsReceivedWaterContent'
      ),
      ...getConfigForImportedTest(
        qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
        'data.Preparation.AssociatedAsReceivedWaterContent'
      ),

      // Liquid Limit
      [`${prefix}.LiquidLimit.DryingTemperature`]: { uom: '°C', format: { nearest: 1 } },
      [`${prefix}.LiquidLimit.Trials.AveragePenetration`]: { uom: 'mm', format: { nearest: 0.1 } },
      [`${prefix}.LiquidLimit.Trials.Penetrations.InitialPenetration`]: { uom: 'mm', format: { nearest: 0.1 } },
      [`${prefix}.LiquidLimit.Trials.Penetrations.FinalPenetration`]: { uom: 'mm', format: { nearest: 0.1 } },
      [`${prefix}.LiquidLimit.Trials.Penetrations.Penetration`]: { uom: 'mm', format: { nearest: 0.1 } },
      ...getConfigForTest(qestIds.moistureContent.ASTM_D_2216.rev2019, 'data.LiquidLimit.Trials.MoistureDetermination'),
      [`${prefix}.LiquidLimit.LiquidLimitPenetration`]: { uom: 'mm', format: { nearest: 0.1 } },
      [`${prefix}.LiquidLimit.WaterContent`]: { format: { nearest: 0.1 } },
      [`${prefix}.LiquidLimit.CorrelationFactor`]: { format: { nearest: 0.001 } },

      [`${prefix}.Preparation.MaterialPercent`]: {
        format: { nearest: 0.1 },
      },

      [`${prefix}.Preparation.StabiliserAmount`]: {
        format: { nearest: 0.1 },
      },

      [`${prefix}.Preparation.EquivalentWaterContent`]: {
        format: { nearest: 0.1 },
      },

      [`${prefix}.Preparation.AssociatedAsReceivedWaterContent.ExternalTest.Results.MoistureContentPercent`]: {
        format: { nearest: 0.1 },
      },

      [`${prefix}.Preparation.SoakingPeriod`]: { uom: 'h', format: { nearest: 0.1 } },

      // Plastic Limit
      [`${prefix}.PlasticLimit.DryingTemperature`]: { uom: '°C', format: { nearest: 1 } },
      ...getConfigForTest(
        qestIds.moistureContent.ASTM_D_2216.rev2019,
        'data.PlasticLimit.Trials.MoistureDetermination'
      ),
      [`${prefix}.Results.PlasticLimit`]: { format: { nearest: 0.1 } },
      [`${prefix}.Results.ConsistencyIndex`]: { format: { nearest: 1 } },
      [`${prefix}.Results.ActivityIndex`]: { format: { type: FormatTypes.SignificantFigures, precision: 2 } },
      [`${prefix}.Results.ActivityIndexCorrectionFactor`]: { format: { nearest: 0.01 } },
      [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
      [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },

      // Results
      [`${prefix}.Results.LiquidLimitPenetration`]: { uom: 'mm', format: { nearest: 0.1 } },
      [`${prefix}.Results.LiquidLimitWaterContent`]: { format: { nearest: 0.1 } },
      [`${prefix}.Results.LiquidLimitCorrelationFactor`]: { format: { nearest: 0.001 } },
    },
    getLabelConfig(prefix),
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    {
      [`${prefix}.Preparation.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
      [`${prefix}.LiquidLimit.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
      [`${prefix}.LiquidLimit.DryMassBalance`]: { defaultReadability: 0.01, defaultUom: 'g' },
      [`${prefix}.PlasticLimit.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
      [`${prefix}.PlasticLimit.DryMassBalance`]: { defaultReadability: 0.01, defaultUom: 'g' },
    }
  );
const PreparationMethodAtterbergLimit = (prefix: string) => {
  return {
    [`${prefix}.Preparation.PreparationMethod`]: {
      options: [
        { label: 'BS 1377-2 Clause 5, 6', value: PreparationMethodType.BS_1377_2 },
        { label: 'BS EN ISO 17892-12', value: PreparationMethodType.BS_EN_ISO },
      ],
    },
  };
};
const PreparationMethodPlasticLimit = (prefix: string) => {
  return {
    [`${prefix}.Preparation.PreparationMethod`]: {
      options: [
        { label: 'BS 1377-2 Clause 6', value: PreparationMethodType.BS_1377_2_Clause_6 },
        { label: 'BS EN ISO 17892-12', value: PreparationMethodType.BS_EN_ISO },
      ],
    },
  };
};
const plasticLimitStandaloneConfig = (prefix: string) =>
  mergeConfig(getConfig(prefix), PreparationMethodPlasticLimit(prefix));
const atterbergLimitConfig = (prefix: string) =>
  mergeConfig(getConfig(prefix), PreparationMethodAtterbergLimit(prefix));

export default {
  [qestIds.atterbergLimit.BS_EN_ISO_17892_12_2018.rev2018_2022_methodA]: atterbergLimitConfig,
  [qestIds.atterbergLimit.BS_EN_ISO_17892_12_2018.rev2018_2022_methodB]: atterbergLimitConfig,
  [qestIds.atterbergLimit.BS_EN_ISO_17892_12_2018.plastic_limit_standalone]: plasticLimitStandaloneConfig,
};
